/**
 * Help-content loader (OPSFL-HELP-1, lane 1a). Lane 1b owns `./content/**` (14 surface docs +
 * `glossary.md`) — those files may NOT exist yet; everything here degrades to "not written" states,
 * never a crash. Content ships in the bundle via `import.meta.glob` raw imports (eager: ~15 small
 * markdown files, no async states to manage).
 */
import { parseGlossary, parseSurfaceDoc, type Glossary, type SurfaceDoc } from './contentModel';
import type { SurfaceId } from './surfaceRegistry';

// Raw markdown, keyed by path like './content/simulations.md'. Empty object until lane 1b merges.
const rawByPath = import.meta.glob('./content/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

function basename(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1);
}

/** All content file basenames present in the bundle (e.g. `simulations.md`, `glossary.md`). */
export function contentFileNames(): string[] {
  return Object.keys(rawByPath).map(basename).sort();
}

const docCache = new Map<string, SurfaceDoc | null>();

/** Parsed surface doc keyed by FILENAME stem (the contract: `content/<surfaceId>.md`), or null. */
export function getSurfaceDoc(surfaceId: SurfaceId | string): SurfaceDoc | null {
  if (docCache.has(surfaceId)) return docCache.get(surfaceId) ?? null;
  const path = `./content/${surfaceId}.md`;
  const raw = rawByPath[path];
  const doc = typeof raw === 'string' ? parseSurfaceDoc(raw) : null;
  docCache.set(surfaceId, doc);
  return doc;
}

let glossaryCache: Glossary | null | undefined;

/** The parsed glossary, or null while `content/glossary.md` does not exist. */
export function getGlossary(): Glossary | null {
  if (glossaryCache === undefined) {
    const raw = rawByPath['./content/glossary.md'];
    glossaryCache = typeof raw === 'string' ? parseGlossary(raw) : null;
  }
  return glossaryCache;
}

/** Look up one glossary term; undefined when the glossary or the term is absent (degrade). */
export function getTerm(id: string) {
  return getGlossary()?.byId.get(id);
}
